/**
 * Ephemeral track layers (#802)
 *
 * A drawn AFF aircraft track joins the LayerRegistry like any other layer, so
 * the drawer's visibility / opacity / z-order controls work on it unchanged.
 * It is created directly rather than through registry.createLayer: the factory
 * table keys on LayerConfig['type'], and adding an 'aff-track' type there would
 * widen two public unions for one runtime-only layer.
 *
 * "Ephemeral" means session-only. The config carries metadata.ephemeral so
 * buildPresetData skips it — a track id embeds Date.now() and could never
 * resolve again from a saved preset or a share link.
 */

import mapboxgl from 'mapbox-gl';
import { LayerRegistry } from '../../layers2';
import { LayerConfig } from '../../layers2/BaseLayer';
import { AffTrackLayer } from '../../layers2/AffTrackLayer';
import { Layer } from '../../../types/layer';

/**
 * Create, register and draw a track layer.
 *
 * @returns the row to place in the drawer's `layers` state
 */
export async function addTrackLayer(
  registry: LayerRegistry,
  map: mapboxgl.Map,
  config: LayerConfig,
): Promise<Layer> {
  const existing = registry.get(config.id);

  if (existing) {
    // Changing the basemap calls setStyle, which drops every custom layer. The
    // track comes back by re-enabling the layer the registry already owns —
    // a second instance for the same id would leave the drawer entry and the
    // map pointing at different objects.
    //
    // A timeframe change arrives the same way, carrying new geometry for the
    // same track (#802 AC7), so the incoming data is applied before re-enabling.
    if (existing instanceof AffTrackLayer) {
      existing.setData(config.config.data);
    }

    await existing.forceReEnable();
    await registry.reorderAllLayers();
    return { ...config } as Layer;
  }

  const layer = new AffTrackLayer(map, config);

  registry.add(layer);
  await layer.initialize();
  await layer.enable();

  // A newly drawn track must obey the drawer's ordering immediately, not just
  // after the next unrelated reorder.
  await registry.reorderAllLayers();

  return { ...config } as Layer;
}

/**
 * Remove a track layer. Destroying it takes its mapbox layers and source off
 * the map. Unknown ids are ignored — Clear Tracks and a per-entry remove can
 * both reach the same id.
 */
export async function removeTrackLayer(registry: LayerRegistry, layerId: string): Promise<void> {
  await registry.remove(layerId);
}

/**
 * Put a track's drawer row into the drawer's rows: appended when new, replaced
 * when the track is already listed.
 *
 * A retime arrives as the same id with a new span (#802 AC7). Keeping the old
 * row kept its old `metadata.span`, so the timeframe control reopened on the
 * window the track was first drawn with, not the one on the map (#866 M4).
 */
export function upsertTrackRow(rows: Layer[], row: Layer): Layer[] {
  return rows.some((l) => l.id === row.id)
    ? rows.map((l) => (l.id === row.id ? row : l))
    : [...rows, row];
}
